import { LOCALES } from '../../config/locales'
import type { CountryCode } from '../../config/locales'
import type { DocType, TdLine, FormState, LineGenerationConfig } from '../../types/testdata.types'

// ---------------------------------------------------------------------------
// Public constants (re-exported by ../useTestDataForm.ts)
// ---------------------------------------------------------------------------

// Fallback NL labels (worden overschreven door land-instelling in de hook)
export const DOC_LABELS: Record<DocType, string> = {
  invoice:         'Factuur',
  order_response:  'Orderbevestiging',
  despatch_advice: 'Pakbon',
  credit_note:     'Creditnota',
}

// Statische fallback — gebruik vatCodeOptions uit de hook voor dynamische opties
export const VAT_CODE_OPTIONS = [
  { value: 'S', label: 'S — Standaard' },
  { value: 'Z', label: 'Z — Nultarief' },
  { value: 'E', label: 'E — Vrijgesteld' },
]

// ---------------------------------------------------------------------------
// localStorage keys (gedeeld tussen de sub-hooks)
// ---------------------------------------------------------------------------

export const LS_VAT_OVERRIDES = 'testdata-vat-overrides'
export const LS_VAT_NEW_CODE  = 'testdata-vat-new-code'
export const LS_VAT_NEW_PCT   = 'testdata-vat-new-pct'
export const LS_COUNTRY       = 'testdata-country'
export const LS_LOCALE        = 'testdata-locale'
export const LS_XML_FORMAT    = 'testdata-xml-format'
export const LS_XML_PROFILE   = 'testdata-xml-profile'
export const LS_FACTURX_PROFILE = 'testdata-facturx-profile'
export const LS_ROUND_UNIT_PRICE       = 'testdata-round-unit-price'
export const LS_ROUND_UNIT_PRICE_VALUE = 'testdata-round-unit-price-value'
export const LS_COMPACT_PDF_LINES      = 'testdata-compact-pdf-lines'
export const LS_INCLUDE_LOCALE_RECOGNITION_TEXT = 'testdata-include-locale-recognition-text'
// REQ-TESTREF-010: renamed from the retired `testdata-include-human-review-pdf`
// key — the merged Human Review PDF flow no longer exists, this is now the
// single central Ground Truth setting shared by download and email.
export const LS_INCLUDE_GROUND_TRUTH = 'testdata-include-ground-truth'
// REQ-TESTDATA-003 (v0.8): persisted precision-test line-generation config —
// a generation-setting, same persistence pattern as compactPdfLines/
// includeGroundTruth above (not e-mail-specific).
export const LS_LINE_GENERATION = 'testdata-line-generation'

export type XmlFormat = 'sales005' | 'ubl21' | 'cii' | 'facturx'
export type XmlProfile = 'none' | 'cius_fr' | 'extended_ctc_fr'
export type FacturxProfile = 'minimum' | 'basic_wl' | 'basic' | 'en16931' | 'extended'

// REQ-TESTDATA-005 (CAP-003 v1.0) — the 8 stable financial-scenario IDs (see
// backend app.services.testdata.financial_scenarios.FINANCIAL_SCENARIO_IDS).
export type FinancialScenarioId =
  | 'FIN-SIMPLE-001' | 'FIN-MULTIVAT-001' | 'FIN-LINE-DISCOUNT-001' | 'FIN-DOC-DISCOUNT-001'
  | 'FIN-CHARGE-001' | 'FIN-ROUNDING-001' | 'FIN-PAYMENT-001' | 'FIN-COMBINED-001'

// DEC-FIN-001 (`XMLInsights-SDD/requirements/testdata-financial-
// reconciliation-v1-0.md`) re-enabled FIN-DOC-DISCOUNT-001: a document
// allowance now targets exactly one fixed semantic VAT category ('standard'),
// resolved server-side — no VAT-category selector is exposed here (not
// user-configurable in v1.0). `disabled` is left available for a future
// scenario/country combination the backend genuinely cannot support (the
// backend still validates and 422s on an unsupported combination, e.g. a
// resolved VAT profile without 'standard' at all — not expected today, but
// the option shape stays generic).
export const FINANCIAL_SCENARIO_OPTIONS: { value: FinancialScenarioId; labelKey: string; disabled?: boolean }[] = [
  { value: 'FIN-SIMPLE-001', labelKey: 'test_data_generator.financial_scenario.simple' },
  { value: 'FIN-MULTIVAT-001', labelKey: 'test_data_generator.financial_scenario.multi_vat' },
  { value: 'FIN-LINE-DISCOUNT-001', labelKey: 'test_data_generator.financial_scenario.line_discount' },
  { value: 'FIN-DOC-DISCOUNT-001', labelKey: 'test_data_generator.financial_scenario.document_discount' },
  { value: 'FIN-CHARGE-001', labelKey: 'test_data_generator.financial_scenario.charges' },
  { value: 'FIN-ROUNDING-001', labelKey: 'test_data_generator.financial_scenario.rounding' },
  { value: 'FIN-PAYMENT-001', labelKey: 'test_data_generator.financial_scenario.payment' },
  { value: 'FIN-COMBINED-001', labelKey: 'test_data_generator.financial_scenario.combined' },
]

export function loadVatOverrides(): Array<{ code: string; percentage: number }> {
  try {
    const raw = localStorage.getItem(LS_VAT_OVERRIDES)
    if (raw) return JSON.parse(raw)
  } catch { /* corrupt */ }
  return []
}

export function emptyForm(country = 'NL'): FormState {
  return {
    country,
    document_number: '', document_date: new Date().toISOString().slice(0, 10),
    project_number: '', buyers_order_number: '', suppliers_order_number: '',
    order_response_number: '', desadv_number: '', end_customer_order_number: '',
    supplier_name: '', supplier_gln: '', supplier_iban: '', supplier_vat: '', supplier_kvk: '',
    supplier_siren: '',
    supplier_attn: '', supplier_address: '', supplier_postal: '', supplier_city: '',
    supplier_phone: '', supplier_email: '', supplier_website: '',
    buyer_name: '', buyer_gln: '', buyer_vat: '',
    buyer_attn: '', buyer_address: '', buyer_postal: '', buyer_city: '',
    invoicer_name: '', invoicer_gln: '', invoicee_name: '', invoicee_gln: '',
    delivery_party_name: '', delivery_party_gln: '',
    ship_from_name: '', ship_from_gln: '',
  }
}

export function defaultLineGenerationConfig(): LineGenerationConfig {
  return {
    mode: 'catalogue',
    line_count: null,
    quantity_mode: 'whole',
    quantity_precision_min: 0,
    quantity_precision_max: 0,
    unit_price_precision_min: 2,
    unit_price_precision_max: 2,
    vary_precision_per_line: false,
  }
}

export function emptyLine(country: CountryCode = 'NL'): TdLine {
  const cfg = LOCALES[country]
  return {
    article_number: '', gtin: '', description: '', quantity: 1, unit: 'ST', unit_price: 0,
    vat_percentage: cfg.defaultVatPct, vat_code: cfg.defaultVatCode,
    quantity_scale: null, unit_price_scale: null,
  }
}

/** REQ-TESTDATA-007 (CAP-003 v1.2) — Scenario -> Normal dematerialization.
 *
 * Switching away from an active Financial Scenario does NOT restore any
 * prior ordinary line set (explicit product decision — there is no hidden
 * backup to restore from). It DOES need to turn the currently-visible,
 * still scenario-materialized rows into genuinely ordinary ones: otherwise
 * they merely LOOK editable while scenario-only canonical fields
 * (`net_amount_override` etc.) silently keep driving totals, and stale
 * `scenario_identity_base` provenance keeps influencing a future scenario
 * re-selection — both violate "what you see is what you generate."
 *
 * This helper preserves every user-visible value exactly (article number,
 * description, quantity, unit, unit price, VAT rate/code, GTIN, and the
 * quantity/unit-price display-precision scale — which is legitimate
 * ordinary/precision-test metadata, not scenario-only) and clears every
 * scenario-only canonical/provenance field, so that afterward:
 * quantity x unit_price is authoritative line economics again; editing any
 * visible field affects generation normally; and a later scenario
 * re-selection derives identity from the CURRENT (possibly user-edited)
 * `article_number`, never a stale pre-Normal value. */
export function dematerializeFinancialScenarioLine(line: TdLine): TdLine {
  return {
    article_number: line.article_number,
    gtin: line.gtin,
    description: line.description,
    quantity: line.quantity,
    unit: line.unit,
    unit_price: line.unit_price,
    vat_percentage: line.vat_percentage,
    vat_code: line.vat_code,
    quantity_scale: line.quantity_scale,
    unit_price_scale: line.unit_price_scale,
    // Scenario-only canonical/provenance metadata — explicitly cleared, not
    // merely left stale. `vat_category` is purely informational for an
    // ordinary line (vat_percentage/vat_code stay authoritative), but is
    // cleared too so no scenario-semantic tag lingers on a now-ordinary row.
    vat_category: null,
    net_amount_override: null,
    gross_line_amount: null,
    line_allowance_amount: null,
    line_charge_amount: null,
    document_allowance_share: null,
    document_charge_share: null,
    vat_amount_override: null,
    scenario_identity_base: null,
  }
}

export function buildDocNumber(type: DocType, initials: string, counter: number): string {
  const now = new Date()
  const yy = String(now.getFullYear()).slice(2)
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  const prefix = type === 'invoice' ? 'INV' : type === 'order_response' ? 'OR' : type === 'credit_note' ? 'CN' : 'DA'
  const init = (initials || 'XX').toUpperCase()
  return `${prefix}-${yy}${mm}${dd}-${init}-${String(counter).padStart(2, '0')}`
}